import { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import clsx from 'clsx';

import { isFilterableDataType } from '@atom/components/common/workOrdersFilters/fieldFilterUtilities';
import WorkOrderFieldFilterInput from '@atom/components/common/workOrdersFilters/WorkOrderFieldFilterInput';
import { GET_CUSTOM_FIELD_FILTERS } from '@atom/graph/workFilters';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Icon, IconButton } from '@atom/mui';
import { KeyboardArrowDownIcon, KeyboardArrowUpIcon } from '@atom/mui/icons';
import { FieldFilter } from '@atom/types/work';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import WorkOrdersFiltersContext, {
  AdditionalFieldFilters,
} from './WorkOrdersFiltersContext';

import classes from './filters.module.css';

const TaskCustomFieldsFilter = () => {
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const { areFiltersDisabled, filterForm, updateFilterForm } = useContext(
    WorkOrdersFiltersContext,
  );
  const { taskCustomCommonFields } = filterForm;

  const preferences = usePreferences();
  const customFieldFilterIds =
    preferences?.workOrders?.filtering?.tasks?.fields;

  const [
    getCustomFieldFilters,
    {
      data: { customFieldFilters: { customFieldFilters, totalCount } } = {
        customFieldFilters: { customFieldFilters: [], totalCount: 0 },
      },
      loading: customFiltersLoading,
    },
  ] = useLazyQuery(GET_CUSTOM_FIELD_FILTERS);

  useEffect(() => {
    if (!isNilOrEmpty(customFieldFilterIds)) {
      getCustomFieldFilters({
        variables: {
          input: {
            ids: customFieldFilterIds,
            page: 1,
            limit: 250,
          },
        },
      });
    }
  }, [customFieldFilterIds]);

  if (
    customFiltersLoading ||
    isNilOrEmpty(customFieldFilters) ||
    totalCount === 0
  ) {
    return null;
  }

  const onFieldFilterChange = (updatedFilter: FieldFilter) =>
    updateFilterForm(
      'taskCustomCommonFields',
      (current: AdditionalFieldFilters) => ({
        ...current,
        [updatedFilter.id]: updatedFilter,
      }),
    );

  return (
    <>
      <div
        className={clsx(
          classes.header,
          classes.filterSectionHeader,
          classes.filterContainer,
        )}
      >
        <div>Common Fields</div>
        <IconButton
          onClick={() => setCollapsed(!collapsed)}
          edge="end"
          data-cy="workOrdersFilterCommonFieldsCollapseButton"
        >
          <Icon
            icon={collapsed ? KeyboardArrowDownIcon : KeyboardArrowUpIcon}
          />
        </IconButton>
      </div>
      {!collapsed &&
        customFieldFilters
          ?.filter(field => isFilterableDataType(field.dataType))
          .map(field => (
            <div className={classes.filterContainer} key={field.id}>
              <WorkOrderFieldFilterInput
                field={field}
                fieldFilter={taskCustomCommonFields[field.id]}
                onChange={onFieldFilterChange}
                disabled={areFiltersDisabled}
                dataCy={`workOrdersFilterCommonField-${field.title}`}
              />
            </div>
          ))}
    </>
  );
};

export default TaskCustomFieldsFilter;
